import React, { useState } from 'react';
import { Trophy, TrendingUp, Flag, Calendar, HelpCircle, Loader2 } from 'lucide-react';
import HelpTip from '../../../components/HelpTip';
import { useScoreboard } from './useScoreboard';
import GoalCards from './GoalCards';
import MonthEntry from './MonthEntry';
import SeasonScorecard from './SeasonScorecard';
import YearSettings from './YearSettings';

const manilaMonth = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' }).slice(0, 7);

const monthLabel = (period) => {
  const [y, m] = (period || '').split('-');
  const names = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return m ? `${names[parseInt(m, 10)]} ${y}` : period;
};

function ScoreHero({ season }) {
  const earned = season?.earned ?? 0;
  const pace = season?.onPace ?? 0;
  let sentence;
  if (earned >= 75) sentence = <>You’ve reached the <b>75 points</b> needed to be a national finalist — great work, keep it up!</>;
  else if (pace >= 75) sentence = <>You need about <b>75 points</b> to become a national finalist. If you keep your current pace, you’ll get there by the end of the year — keep going!</>;
  else sentence = <>You need about <b>75 points</b> to become a national finalist. If you keep your current pace, you’ll finish the year at about <b>{pace}</b> — a little more will get you there.</>;

  let pill;
  if (pace >= 75) pill = { t: 'On track to qualify', c: 'bg-emerald-100 text-emerald-800', Icon: Trophy };
  else if (pace >= 60) pill = { t: 'Getting close', c: 'bg-sky-100 text-sky-800', Icon: TrendingUp };
  else pill = { t: 'Keep building your score', c: 'bg-amber-100 text-amber-800', Icon: Flag };
  const PillIcon = pill.Icon;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg,#0f2a4a,#1d4ed8,#0ea5e9)' }} />
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg grid place-items-center text-white" style={{ background: '#0f2a4a' }}><Trophy className="h-4 w-4" /></div>
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-1.5">Your Best PESO score this year <HelpTip id="award" /></h2>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="text-center sm:text-left shrink-0">
          <div className="leading-none"><span className="text-6xl font-black text-slate-900 tabular-nums">{earned}</span><span className="text-2xl text-slate-400 font-bold"> / 100</span></div>
          <div className="text-sm text-slate-500 font-medium mt-1">points earned so far</div>
          <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${pill.c}`}><PillIcon className="h-3.5 w-3.5" /> {pill.t}</div>
        </div>
        <div className="flex-1 w-full">
          <div className="relative h-9 rounded-lg bg-slate-100 overflow-hidden">
            <div className="absolute inset-y-0 left-0 rounded-lg" style={{ width: `${Math.min(100, earned)}%`, background: 'linear-gradient(90deg,#0f2a4a,#1d4ed8)' }} />
            <div className="absolute inset-y-0 w-1 bg-rose-500" style={{ left: '75%' }} />
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-xs text-slate-600">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: '#1d4ed8' }} /> Your score: <b className="text-slate-900">{earned}</b></span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-3.5 w-1 rounded bg-rose-500" /> Goal to qualify: <b className="text-rose-600">75 points</b></span>
          </div>
          <p className="text-sm text-slate-600 mt-2">{sentence}</p>
        </div>
      </div>
    </div>
  );
}

function Explainer() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><HelpCircle className="h-4 w-4 text-blue-700" /> Where your score comes from — 3 kinds of information</p>
      <div className="grid sm:grid-cols-3 gap-2 text-[11px] leading-snug">
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2.5"><span className="font-bold text-emerald-700">1. Monthly numbers</span> — the platform counts what it sees automatically; <b>you add</b> walk-ins &amp; off-platform work. <span className="text-slate-500 block mt-0.5">Feeds placement, LMI &amp; career-guidance points.</span></div>
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-2.5"><span className="font-bold text-blue-700">2. Programs, awards, leadership</span> — the platform can’t see these, so <b>you enter</b> them. <span className="text-slate-500 block mt-0.5">Earns programs, awards &amp; leadership points.</span></div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5"><span className="font-bold text-slate-600">3. Your monthly goals</span> — <b>DOLE-fixed</b> where DOLE sets a number; otherwise your own <b>SPRS annual target ÷ 12</b>. <span className="text-slate-500 block mt-0.5">Each goal card is badged so you know which.</span></div>
      </div>
    </div>
  );
}

/**
 * The Best-PESO "Goals" tab — the PESO dashboard home. Composes the score hero, this
 * month's goal cards + data entry, the season points scorecard, and the yearly award
 * inputs, all off one useScoreboard load. Monthly numbers write through the SPRS report
 * path (one source of truth); award inputs write to peso_scorecard.
 */
export default function GoalsTab({ authHeaders }) {
  const [period, setPeriod] = useState(manilaMonth);
  const { board, loading, loadError, savingYear, savingMonth, saveYear, saveMonth } = useScoreboard(authHeaders, period);

  const settings = board?.settings || {};
  // The save is a top-level merge, so each surface sends only the keys it owns:
  // the LMI toggle patches lmi_on_time; the yearly panel patches programs/awards/targets.
  const setLmi = (val) => saveYear({ lmi_on_time: { ...(settings.lmi_on_time || {}), [period]: val } });

  return (
    <div className="space-y-5">
      {board && <ScoreHero season={board.season} />}
      <Explainer />

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-blue-700" />
          <label htmlFor="goals-month" className="text-sm font-semibold text-slate-700">Month you’re viewing / editing:</label>
          <input id="goals-month" type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="text-sm border border-slate-300 rounded-md h-9 px-2 bg-white font-semibold" />
        </div>
        <span className="text-[11px] text-slate-400">Pick any month — even before you started using Bohol Trabaho.</span>
      </div>

      {loading && <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>}
      {loadError && !loading && <div className="text-sm text-rose-600 py-6 text-center">Couldn’t load the scoreboard. Try Refresh.</div>}

      {board && !loading && (
        <>
          <div>
            <h3 className="text-sm font-bold text-slate-900 mb-2">{monthLabel(period)} — progress vs your goals</h3>
            <GoalCards monthly={board.monthly} />
          </div>

          <MonthEntry
            monthly={board.monthly}
            monthLabel={monthLabel(period)}
            saving={savingMonth}
            onSaveNumbers={saveMonth}
            lmiThisMonth={board.monthly?.lmi_this_month ?? null}
            onSetLmi={setLmi}
          />

          <SeasonScorecard season={board.season} />

          <YearSettings settings={settings} saving={savingYear} onSave={saveYear} />
        </>
      )}

      <p className="text-[11px] text-slate-400 text-center">
        Scoring verified against the DOLE-9 “Points System Reference”. Monthly numbers are the same data as your DOLE SPRS report.
      </p>
    </div>
  );
}
